import axios, { AxiosError, AxiosResponse } from "axios";

import { IForgotPasswordFormValues } from "../@types/ForgotPassword";
import {
  IAuthAPIResponse,
  IConfirmLoginMfa,
  ILogin,
  ILoginResponse,
  IToken,
} from "../@types/Login";
import { ISetPasswordRequest, IPasswordPolicy } from "../@types/SetPassword";
import { api } from "./api";

export const authService = {
  async authenticate({ email, password }: ILogin): Promise<any> {
    const responseFromAPI = await api.post<IAuthAPIResponse>(
      "/Portal/Authentication/Login",
      {
        username: email,
        password,
      },
      // EN-5255: a 401 here means bad credentials, not an expired session, so
      // skip the interceptor's refresh/redirect and let the login page show it.
      { anonymous: true }
    );

    const { data } = responseFromAPI;

    return {
      token: data.token,
      preferences: data.preferences,
      portalPreferences: data.portalPreferences,
      mfaDetails: data.mfaDetails,
      intercomDetails: data.intercomDetails,
      userDetails: data.userDetails,
      hospitalConfiguration: data.hospitalConfiguration,
      errors: data.errors,
      staffPreferences: data.staffPreferences,
      wardConfiguration: data.wardConfiguration,
      tenantDetails: data.tenantDetails,
    } as ILoginResponse;
  },

  async confirmLoginMfa({
    email,
    code,
  }: IConfirmLoginMfa): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/ConfirmLoginMfa";

    const serverResponse = await api
      .post(url, { email, code })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async sendStaffForgotPasswordRequest({
    email,
  }: IForgotPasswordFormValues): Promise<void> {
    const url = "/Portal/Authentication/ForgotPassword";
    await api.post(url, { username: email });
  },

  async sendPatientForgotPasswordRequest({
    email,
  }: IForgotPasswordFormValues): Promise<void> {
    const url = "/Mobile/Authentication/ForgotPassword";
    await api.post(url, { username: email });
  },

  async setPassword({
    password,
    email,
    code,
  }: ISetPasswordRequest): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/SetPassword";

    const serverResponse = await api
      .post(url, {
        username: email,
        newPassword: password,
        confirmationCode: code,
      })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async getPasswordPolicy(): Promise<IPasswordPolicy | undefined> {
    const url = "/Shared/Authentication/PasswordPolicy";

    try {
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  },

  async validateTokenCode(
    username: string,
    code: string
  ): Promise<AxiosResponse> {
    const url = `/Portal/Authentication/ValidateTokenCode`;
    const isValid = await api.post(url, {
      username,
      confirmationCode: code,
    });
    return isValid;
  },

  async refreshToken(refreshToken: string): Promise<AxiosResponse<IToken>> {
    const url = `/Portal/Authentication/RefreshToken`;
    const response = await api.post<IToken>(url, refreshToken);

    return response;
  },

  // Revokes the Cognito refresh token server-side so the axios refresh
  // interceptor cannot silently re-authenticate after the user signs out.
  // Soft-fails to avoid blocking the client-side logout if the endpoint is
  // unavailable.
  async signOutSession(refreshToken: string): Promise<void> {
    try {
      await api.post("/Portal/Authentication/Logout", { refreshToken });
    } catch (error) {
      console.warn("Failed to revoke refresh token on sign out", error);
    }
  },

  // mobile

  async getMobileIdToken({ email, password }: ILogin): Promise<{
    idToken: string;
    patientUserId?: number;
    errors?: any[];
  }> {
    const responseFromAPI = await api.post<any>(
      "/Mobile/Authentication/Login",
      {
        username: email,
        password,
      }
    );

    const { data } = responseFromAPI;

    return {
      idToken: data.token.idToken,
      patientUserId: data.patient?.userId,
      errors: data.errors,
    };
  },
};
